import type { UnprefixedOrganizationNumber } from '#shared/organization-number'
import type { Brand, GenericSchema, InferOutput } from 'valibot'
import { joinURL } from 'ufo'
import {
  array,
  boolean,

  nullish,
  number,
  object,
  parse,
  picklist,
  string,
} from 'valibot'

export const BRREG_BASE_URL = 'https://data.brreg.no/enhetsregisteret/api/enheter'

export function brregKodeBeskrivelseSchema<const T extends GenericSchema>(schema: T) {
  return object({
    kode: nullish(schema),
    beskrivelse: nullish(string()),
  })
}

export const brregAdresseSchema = object({
  kommune: nullish(string()),
  landkode: nullish(string()),
  postnummer: nullish(string()),
  adresse: nullish(array(string())),
  land: nullish(string()),
  kommunenummer: nullish(string()),
  poststed: nullish(string()),
})

export type BrregAdresse = InferOutput<typeof brregAdresseSchema>

// https://www.brreg.no/bedrift/organisasjonsformer/
export const brregOrganisasjonsformSchema = picklist([
  'AAFY',
  'ADOS',
  'ANNA',
  'ANS',
  'AS',
  'ASA',
  'BA',
  'BBL',
  'BEDR',
  'BO',
  'BRL',
  'DA',
  'ENK',
  'EOFG',
  'ESEK',
  'FKF',
  'FLI',
  'FYLK',
  'GFS',
  'IKJP',
  'IKS',
  'KBO',
  'KF',
  'KIRK',
  'KOMM',
  'KS',
  'KTRF',
  'NUF',
  'OPMV',
  'ORGL',
  'PERS',
  'PK',
  'PRE',
  'SA',
  'SAM',
  'SE',
  'SF',
  'SPA',
  'STAT',
  'STI',
  'SÆR',
  'TVAM',
  'UTLA',
  'VPFO',
])

export const brregEnhetSchema = object({
  organisasjonsnummer: string(),
  navn: string(),
  postadresse: nullish(brregAdresseSchema),
  forretningsadresse: nullish(brregAdresseSchema),
  naeringskode1: nullish(brregKodeBeskrivelseSchema(string())),
  naeringskode2: nullish(brregKodeBeskrivelseSchema(string())),
  naeringskode3: nullish(brregKodeBeskrivelseSchema(string())),
  antallAnsatte: nullish(number()),
  harRegistrertAntallAnsatte: nullish(boolean(), false),
  organisasjonsform: nullish(brregKodeBeskrivelseSchema(brregOrganisasjonsformSchema)),
  telefon: nullish(string()),
  mobil: nullish(string()),
  hjemmeside: nullish(string()),
  // A bunch of fields are omitted here, until we need them
})

export type BrregEnhet = InferOutput<typeof brregEnhetSchema>

export async function fetchOrganizationFromBrreg(organizationNumber: UnprefixedOrganizationNumber, signal?: AbortSignal) {
  const res = await $fetch(joinURL(BRREG_BASE_URL, organizationNumber), {
    signal,
    onResponse: convert404to204,
  })

  return parse(nullish(brregEnhetSchema, null), res)
}

/**
 * Format a Brreg address into a single line string.
 *
 * @param address - The Brreg address to format.
 */
export function formatAddress(address: BrregAdresse | undefined | null) {
  const base = address?.adresse?.join(' ')

  const city = [address?.postnummer, address?.poststed].filter(Boolean).join(' ')

  return [base, city].filter(Boolean).join(', ')
}

/**
 * Get the primary industry code for a Brreg unit.
 *
 * @param unit - The Brreg unit to get the primary industry code for.
 */
export function getPrimaryIndustryCode(unit: BrregEnhet | undefined | null) {
  return unit?.naeringskode1 ?? unit?.naeringskode2 ?? unit?.naeringskode3 ?? null
}

/**
 * Some entities should be handled differently based on their industry code.
 *
 * This function creates a type guard that checks if a Brreg unit has an industry code that starts with any of the given prefixes.
 *
 * @see {@link https://www.ssb.no/klass/klassifikasjoner/6}
 *
 * @param prefixes - The industry code prefixes to check for.
 * @returns - A type guard that checks if a Brreg unit has an industry code that starts with any of the given prefixes.
 */
function brregCodePrefixPredicate<const TBrand extends string>(...prefixes: string[]) {
  return (unit: BrregEnhet | undefined | null): unit is BrregEnhet & Brand<TBrand> => {
    const industryCode = getPrimaryIndustryCode(unit)
    return prefixes.some(prefix => industryCode?.kode?.startsWith(prefix))
  }
}

/**
 * @see {@link https://acoundervisning.atlassian.net/wiki/spaces/EOW/pages/2997616665/N+ringskoder+for+nettbutikken}
 */
export const isLegalEntity = brregCodePrefixPredicate<'legal-entity'>('69.', '84.23', '84.24')

export const isHigherEducationEntity = brregCodePrefixPredicate<'higher-education-entity'>('85.4')
